import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { PageHeader } from '@/components/ui-bits';
import { Save, Settings as Cog, QrCode, Cloud, Unplug, RefreshCw, Smartphone, Plus, Trash2, Shield, ContactRound } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth-context';
import { loadGlossary } from '@/lib/glossary';

const EMPTY_WA = {
  mode: 'cloud_api',
  phone_number_id: '',
  access_token: '',
  business_account_id: '',
  display_phone: '',
  active: true,
};

const GLOSSARY_KEYS = [
  ['lead', 'Lead (singular)'],
  ['leads', 'Leads (plural)'],
  ['deal', 'Deal (singular)'],
  ['deals', 'Deals (plural)'],
  ['company', 'Empresa (singular)'],
  ['companies', 'Empresas (plural)'],
  ['pipeline', 'Pipeline'],
  ['stage', 'Etapa'],
  ['task', 'Tarea'],
  ['tasks', 'Tareas'],
  ['campaign', 'Campaña'],
  ['campaigns', 'Campañas'],
];

export default function SettingsPage() {
  const { tenant, refresh, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [waCfg, setWaCfg] = useState({ ...EMPTY_WA });
  const [qrSession, setQrSession] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [contactSync, setContactSync] = useState(null);
  const [contactSyncing, setContactSyncing] = useState(false);
  const [defaultAssignee, setDefaultAssignee] = useState('');
  const [tz, setTz] = useState('America/Mexico_City');
  const [quiet, setQuiet] = useState({ enabled: false, start_hour: 21, end_hour: 9, timezone: 'America/Mexico_City' });
  const [pwd, setPwd] = useState({ current: '', next: '' });
  const [glossary, setGlossary] = useState({});
  const [dupRules, setDupRules] = useState({ by_phone: true, by_email: false, action: 'reject' });
  const [visibility, setVisibility] = useState({ mode: 'role_default', by_role: {} });
  const [requireLoss, setRequireLoss] = useState(false);
  const [lossReasons, setLossReasons] = useState([]);
  const [newLoss, setNewLoss] = useState('');
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [totpSetup, setTotpSetup] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [totpPwd, setTotpPwd] = useState('');
  const [totpBusy, setTotpBusy] = useState(false);
  const [chatSla, setChatSla] = useState({
    warn_minutes: 5,
    critical_minutes: 15,
    target_first_reply_minutes: 5,
    auto_assign_unassigned: false,
    prefer_skill_match: true,
    auto_assign_user_ids: [],
    business_hours_enabled: false,
    business_hours_start: 9,
    business_hours_end: 18,
    business_days: [0, 1, 2, 3, 4],
    notify_on_breach: true,
    notify_on_warn: false,
  });

  const loadQrStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/whatsapp/qr/status');
      setQrSession(data.session || null);
    } catch {
      setQrSession(null);
    }
  }, []);

  const loadContactSync = useCallback(async () => {
    try {
      const { data } = await api.get('/whatsapp/contacts/sync-status');
      setContactSync(data);
    } catch {
      setContactSync(null);
    }
  }, []);

  const loadLossReasons = useCallback(async () => {
    try {
      const { data } = await api.get('/catalogs?kind=loss_reason&active_only=false');
      setLossReasons(data.items || []);
    } catch {
      setLossReasons([]);
    }
  }, []);

  useEffect(() => {
    api.get('/admin/users').then((r) => setUsers(r.data.users));
    api.get('/admin/settings').then((r) => {
      setDefaultAssignee(r.data.tenant?.default_assignee || '');
      setTz(r.data.tenant?.settings?.timezone || 'America/Mexico_City');
      const qh = r.data.tenant?.settings?.campaign_quiet_hours;
      if (qh) setQuiet({ enabled: !!qh.enabled, start_hour: qh.start_hour ?? 21, end_hour: qh.end_hour ?? 9, timezone: qh.timezone || 'America/Mexico_City' });
    });
    api.get('/admin/config').then((r) => {
      setGlossary(r.data.glossary || {});
      setDupRules(r.data.duplicate_rules || { by_phone: true, by_email: false, action: 'reject' });
      setVisibility(r.data.lead_visibility || { mode: 'role_default', by_role: {} });
      setRequireLoss(!!r.data.require_loss_reason);
      if (r.data.chat_sla) {
        const s = r.data.chat_sla;
        setChatSla({
          warn_minutes: s.warn_minutes ?? 5,
          critical_minutes: s.critical_minutes ?? 15,
          target_first_reply_minutes: s.target_first_reply_minutes ?? 5,
          auto_assign_unassigned: !!s.auto_assign_unassigned,
          prefer_skill_match: s.prefer_skill_match !== false,
          auto_assign_user_ids: s.auto_assign_user_ids || [],
          business_hours_enabled: !!s.business_hours_enabled,
          business_hours_start: s.business_hours_start ?? 9,
          business_hours_end: s.business_hours_end ?? 18,
          business_days: s.business_days || [0, 1, 2, 3, 4],
          notify_on_breach: s.notify_on_breach !== false,
          notify_on_warn: !!s.notify_on_warn,
        });
      }
    }).catch(() => {});
    loadLossReasons();
    api.get('/auth/2fa/status').then((r) => setTotpEnabled(!!r.data.enabled)).catch(() => {});
    api.get('/whatsapp/config').then((r) => {
      if (r.data.config) {
        setWaCfg({ ...EMPTY_WA, ...r.data.config, access_token: r.data.config.access_token || '' });
      }
      if (r.data.qr_status) setQrSession(r.data.qr_status);
    }).catch(() => {});
    loadContactSync();
    // eslint-disable-next-line
  }, []);

  // Poll QR session while waiting for scan
  useEffect(() => {
    if (waCfg.mode !== 'qr') return undefined;
    const status = qrSession?.status;
    if (status === 'qr' || status === 'connecting') {
      const t = setInterval(loadQrStatus, 2500);
      return () => clearInterval(t);
    }
    return undefined;
  }, [waCfg.mode, qrSession?.status, loadQrStatus]);

  const saveSettings = async () => {
    try {
      await api.patch('/admin/settings', {
        default_assignee: defaultAssignee || null,
        timezone: tz,
        campaign_quiet_hours: quiet,
        glossary,
        duplicate_rules: dupRules,
        lead_visibility: visibility,
        require_loss_reason: requireLoss,
        chat_sla: chatSla,
      });
      await loadGlossary(true);
      toast.success('Ajustes guardados');
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    }
  };

  const addLossReason = async () => {
    if (!newLoss.trim()) return;
    try {
      await api.post('/catalogs', { kind: 'loss_reason', label: newLoss.trim() });
      setNewLoss('');
      loadLossReasons();
      toast.success('Motivo agregado');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const removeLossReason = async (id) => {
    if (!confirm('¿Eliminar este motivo?')) return;
    try {
      await api.delete(`/catalogs/${id}`);
      loadLossReasons();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const seedLoss = async () => {
    try {
      const { data } = await api.post('/catalogs/loss_reason/seed-defaults');
      toast.success(data.created ? `${data.created} motivos creados` : 'Catálogo listo');
      loadLossReasons();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';

  const changePassword = async () => {
    if (pwd.next.length < 8) return toast.error('Mínimo 8 caracteres');
    try {
      await api.post('/auth/change-password', { current_password: pwd.current, new_password: pwd.next });
      toast.success('Contraseña actualizada');
      setPwd({ current: '', next: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const setMode = (mode) => {
    setWaCfg((prev) => ({ ...prev, mode }));
    if (mode === 'qr') loadQrStatus();
  };

  const saveWa = async () => {
    try {
      const payload = { ...waCfg, mode: waCfg.mode || 'cloud_api', active: true };
      if (payload.mode === 'qr') {
        payload.phone_number_id = payload.phone_number_id || null;
        payload.access_token = payload.access_token || null;
      }
      await api.put('/whatsapp/config', payload);
      toast.success('WhatsApp guardado');
      if (payload.mode === 'qr') await loadQrStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const removeWa = async () => {
    if (!confirm('¿Remover configuración de WhatsApp? Se usará WhatsApp Web como fallback.')) return;
    try {
      await api.delete('/whatsapp/config');
      setWaCfg({ ...EMPTY_WA });
      setQrSession(null);
      toast.success('Removido');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const syncPhoneContacts = async () => {
    setContactSyncing(true);
    try {
      const { data } = await api.post('/whatsapp/contacts/sync');
      setContactSync(data.status || null);
      const n = data.bridge?.total ?? data.status?.bridge_contacts_cached ?? 0;
      toast.success(data.message || `Sincronizados ${n} contactos del teléfono`);
      await loadContactSync();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo sincronizar contactos');
    } finally {
      setContactSyncing(false);
    }
  };

  const connectQr = async (mode = 'soft') => {
    // mode: 'soft' | 'new_qr'
    // CRITICAL: never spam force_new while pairing — it wipes creds after scan → 401 disconnect
    setQrLoading(true);
    try {
      await api.put('/whatsapp/config', {
        mode: 'qr',
        active: true,
        phone_number_id: null,
        access_token: null,
        display_phone: waCfg.display_phone || null,
        business_account_id: null,
      });
      setWaCfg((prev) => ({ ...prev, mode: 'qr', active: true }));

      const isNew = mode === 'new_qr';
      // Hard wipe only when user explicitly wants a brand-new QR and we are not connected
      const payload = {
        force_new: isNew,
        confirm_wipe: isNew && qrSession?.status !== 'connected' && qrSession?.status !== 'connecting',
      };
      if (isNew && (qrSession?.status === 'connected' || qrSession?.status === 'connecting')) {
        toast.message('Primero desvincula', {
          description: 'Si la sesión está conectando o ya vinculada, pulsa Desvincular y luego Nuevo QR.',
        });
      }

      const { data } = await api.post('/whatsapp/qr/connect', payload);
      let session = data.session || null;
      setQrSession(session);

      // Poll status — do NOT call connect again (would kill pairing)
      for (let i = 0; i < 40; i++) {
        if (session?.status === 'qr' && session?.qr_data_url) break;
        if (session?.status === 'connected') break;
        await new Promise((r) => setTimeout(r, 1000));
        try {
          const st = await api.get('/whatsapp/qr/status');
          session = st.data.session || session;
          setQrSession(session);
        } catch {
          /* ignore */
        }
      }

      if (session?.status === 'connected') {
        toast.success('WhatsApp conectado y estable');
        setTimeout(() => loadContactSync(), 5000);
      } else if (session?.status === 'qr' && session?.qr_data_url) {
        toast.info('Escanea el QR y espera «Conectado». No vuelvas a pulsar botones.');
      } else if (session?.status === 'connecting') {
        toast.message('Vinculando…', {
          description: 'Si ya escaneaste, espera 15–30 s sin pulsar nada.',
        });
      } else if (session?.last_error) {
        toast.error(session.last_error);
      } else {
        toast.message('Sin QR todavía', {
          description: 'Espera 10 s y pulsa «Nuevo QR» una sola vez.',
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo iniciar QR');
    } finally {
      setQrLoading(false);
      loadQrStatus();
    }
  };

  const disconnectQr = async () => {
    if (!confirm('¿Desvincular WhatsApp de esta empresa? Podrás generar un QR nuevo después.')) return;
    setQrLoading(true);
    try {
      const { data } = await api.post('/whatsapp/qr/disconnect');
      setQrSession(data.session || { status: 'disconnected' });
      toast.success('WhatsApp desvinculado. Ya puedes pulsar Nuevo QR.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al desvincular');
    } finally {
      setQrLoading(false);
    }
  };

  const statusLabel = {
    connected: 'Conectado',
    qr: 'Esperando escaneo',
    connecting: 'Conectando…',
    disconnected: 'Desconectado',
    bridge_unavailable: 'Servicio QR no disponible',
  };

  const statusColor = {
    connected: 'text-emerald-600',
    qr: 'text-amber-600',
    connecting: 'text-sky-600',
    disconnected: 'text-muted-foreground',
    bridge_unavailable: 'text-[hsl(var(--danger))]',
  };

  return (
    <div>
      <PageHeader title="Ajustes" description="Configuración de tu empresa." />
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="surface p-5">
          <h2 className="font-display font-semibold flex items-center gap-2"><Cog className="h-4 w-4" /> Workspace</h2>
          <div className="mt-3 space-y-3 text-sm">
            <div><span className="text-xs text-muted-foreground">Empresa</span><div className="font-medium">{tenant?.name}</div></div>
            <div><span className="text-xs text-muted-foreground">Slug</span><div className="font-mono text-xs">{tenant?.slug}</div></div>
            <label>Asignación por defecto<select value={defaultAssignee} onChange={(e) => setDefaultAssignee(e.target.value)} className="input mt-1">
              <option value="">—</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            </select></label>
            <label>Zona horaria<input value={tz} onChange={(e) => setTz(e.target.value)} className="input mt-1" /></label>
            <div className="border border-border rounded-[10px] p-3 space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={quiet.enabled} onChange={(e) => setQuiet({ ...quiet, enabled: e.target.checked })} />
                Horario silencioso en campañas
              </label>
              <p className="text-[11px] text-muted-foreground">No enviar masivos entre estas horas (zona MX). Ej. 21 → 9.</p>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs">Desde (h)
                  <input type="number" min={0} max={23} className="input mt-1" value={quiet.start_hour} onChange={(e) => setQuiet({ ...quiet, start_hour: Number(e.target.value) })} />
                </label>
                <label className="text-xs">Hasta (h)
                  <input type="number" min={0} max={23} className="input mt-1" value={quiet.end_hour} onChange={(e) => setQuiet({ ...quiet, end_hour: Number(e.target.value) })} />
                </label>
              </div>
            </div>
            <div className="border border-border rounded-[10px] p-3 space-y-2">
              <h3 className="text-sm font-medium">SLA de chat / cola</h3>
              <p className="text-[11px] text-muted-foreground">
                Umbrales de espera en Inbox, objetivo de 1ª respuesta en Reportes y auto-asignación round-robin.
              </p>
              <div className="grid grid-cols-3 gap-2">
                <label className="text-xs">Alerta (min)
                  <input
                    type="number"
                    min={1}
                    max={1440}
                    className="input mt-1"
                    value={chatSla.warn_minutes}
                    onChange={(e) => setChatSla({ ...chatSla, warn_minutes: Number(e.target.value) || 1 })}
                  />
                </label>
                <label className="text-xs">Crítico (min)
                  <input
                    type="number"
                    min={1}
                    max={1440}
                    className="input mt-1"
                    value={chatSla.critical_minutes}
                    onChange={(e) => setChatSla({ ...chatSla, critical_minutes: Number(e.target.value) || 1 })}
                  />
                </label>
                <label className="text-xs">Objetivo 1ª resp.
                  <input
                    type="number"
                    min={1}
                    max={1440}
                    className="input mt-1"
                    value={chatSla.target_first_reply_minutes}
                    onChange={(e) => setChatSla({ ...chatSla, target_first_reply_minutes: Number(e.target.value) || 1 })}
                  />
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={chatSla.auto_assign_unassigned}
                  onChange={(e) => setChatSla({ ...chatSla, auto_assign_unassigned: e.target.checked })}
                />
                Auto-asignar conversaciones sin dueño (round-robin)
              </label>
              {chatSla.auto_assign_unassigned && (
                <>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={chatSla.prefer_skill_match !== false}
                      onChange={(e) => setChatSla({ ...chatSla, prefer_skill_match: e.target.checked })}
                    />
                    Preferir agentes con skill del lead
                  </label>
                  <label className="text-xs block">
                    Agentes en el pool (vacío = todos activos)
                    <select
                      multiple
                      className="input mt-1 min-h-[100px]"
                      value={chatSla.auto_assign_user_ids}
                      onChange={(e) => setChatSla({
                        ...chatSla,
                        auto_assign_user_ids: [...e.target.selectedOptions].map((o) => o.value),
                      })}
                    >
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>{u.name || u.email} ({u.role})</option>
                      ))}
                    </select>
                  </label>
                </>
              )}
              <div className="border-t border-border/60 pt-2 space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={chatSla.business_hours_enabled}
                    onChange={(e) => setChatSla({ ...chatSla, business_hours_enabled: e.target.checked })}
                  />
                  Solo alertar en horario laboral
                </label>
                {chatSla.business_hours_enabled && (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="text-xs">Desde (h)
                        <input
                          type="number"
                          min={0}
                          max={23}
                          className="input mt-1"
                          value={chatSla.business_hours_start}
                          onChange={(e) => setChatSla({ ...chatSla, business_hours_start: Number(e.target.value) })}
                        />
                      </label>
                      <label className="text-xs">Hasta (h)
                        <input
                          type="number"
                          min={0}
                          max={24}
                          className="input mt-1"
                          value={chatSla.business_hours_end}
                          onChange={(e) => setChatSla({ ...chatSla, business_hours_end: Number(e.target.value) })}
                        />
                      </label>
                    </div>
                    <div className="flex flex-wrap gap-2 text-[11px]">
                      {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((label, i) => {
                        const on = (chatSla.business_days || []).includes(i);
                        return (
                          <button
                            key={label}
                            type="button"
                            className={`px-2 py-1 rounded border ${on ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}
                            onClick={() => {
                              const set = new Set(chatSla.business_days || []);
                              if (set.has(i)) set.delete(i);
                              else set.add(i);
                              setChatSla({ ...chatSla, business_days: [...set].sort() });
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={chatSla.notify_on_breach !== false}
                    onChange={(e) => setChatSla({ ...chatSla, notify_on_breach: e.target.checked })}
                  />
                  Notificar al romper SLA crítico (push + in-app)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!chatSla.notify_on_warn}
                    onChange={(e) => setChatSla({ ...chatSla, notify_on_warn: e.target.checked })}
                  />
                  Notificar también en umbral de alerta
                </label>
              </div>
            </div>
            <button data-testid="settings-save" onClick={saveSettings} className="btn-primary"><Save className="h-4 w-4" /> Guardar</button>
            <div className="border-t border-border pt-3 space-y-2">
              <h3 className="text-sm font-medium">Cambiar mi contraseña</h3>
              <input type="password" className="input" placeholder="Actual" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} />
              <input type="password" className="input" placeholder="Nueva (mín. 8)" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} />
              <button type="button" className="btn-ghost" onClick={changePassword}>Actualizar contraseña</button>
            </div>

            <div className="border-t border-border pt-3 space-y-2">
              <h3 className="text-sm font-medium flex items-center gap-2">
                <Shield className="h-4 w-4" /> Autenticación en 2 pasos (2FA)
              </h3>
              <p className="text-[11px] text-muted-foreground">
                Usa Google Authenticator, Authy o similar. Estado: <b>{totpEnabled ? 'Activo' : 'Desactivado'}</b>
                {user?.totp_enabled ? ' · sesión' : ''}
              </p>
              {!totpEnabled && !totpSetup && (
                <button
                  type="button"
                  className="btn-primary"
                  disabled={totpBusy}
                  onClick={async () => {
                    setTotpBusy(true);
                    try {
                      const { data } = await api.post('/auth/2fa/setup');
                      setTotpSetup(data);
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Error');
                    } finally { setTotpBusy(false); }
                  }}
                >
                  Activar 2FA
                </button>
              )}
              {totpSetup && (
                <div className="rounded-[10px] border border-border p-3 space-y-2 text-xs">
                  <p>1. Escanea el QR en tu app autenticadora:</p>
                  {totpSetup.qr_data_url && (
                    <div className="flex justify-center py-2">
                      <img
                        src={totpSetup.qr_data_url}
                        alt="QR 2FA"
                        className="w-[180px] h-[180px] rounded-[12px] bg-white p-2 border border-border"
                      />
                    </div>
                  )}
                  <p className="text-muted-foreground">O ingresa la clave manual:</p>
                  <code className="block font-mono text-[11px] break-all bg-secondary/40 p-2 rounded-[8px]">{totpSetup.secret}</code>
                  <p>2. Escribe el código de 6 dígitos para confirmar:</p>
                  <input
                    className="input font-mono tracking-widest"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    placeholder="000000"
                    maxLength={8}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={totpBusy}
                      onClick={async () => {
                        setTotpBusy(true);
                        try {
                          await api.post('/auth/2fa/enable', { code: totpCode });
                          toast.success('2FA activado');
                          setTotpEnabled(true);
                          setTotpSetup(null);
                          setTotpCode('');
                          refresh();
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || 'Código inválido');
                        } finally { setTotpBusy(false); }
                      }}
                    >
                      Confirmar y activar
                    </button>
                    <button type="button" className="btn-ghost" onClick={() => { setTotpSetup(null); setTotpCode(''); }}>Cancelar</button>
                  </div>
                </div>
              )}
              {totpEnabled && (
                <div className="space-y-2">
                  <input type="password" className="input" placeholder="Tu contraseña" value={totpPwd} onChange={(e) => setTotpPwd(e.target.value)} />
                  <input className="input font-mono tracking-widest" placeholder="Código 2FA" value={totpCode} onChange={(e) => setTotpCode(e.target.value)} maxLength={8} />
                  <button
                    type="button"
                    className="btn-ghost text-[hsl(var(--danger))]"
                    disabled={totpBusy}
                    onClick={async () => {
                      setTotpBusy(true);
                      try {
                        await api.post('/auth/2fa/disable', { password: totpPwd, code: totpCode });
                        toast.success('2FA desactivado');
                        setTotpEnabled(false);
                        setTotpPwd('');
                        setTotpCode('');
                        refresh();
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Error');
                      } finally { setTotpBusy(false); }
                    }}
                  >
                    Desactivar 2FA
                  </button>
                </div>
              )}
            </div>

            <div className="border-t border-border pt-3 space-y-2">
              <h3 className="text-sm font-medium">Notificaciones push</h3>
              <p className="text-[11px] text-muted-foreground">
                Recibe alertas aunque el CRM esté cerrado (Chrome/Edge/Firefox). Se activan al iniciar sesión si das permiso.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={async () => {
                    try {
                      const { ensurePushSubscription } = await import('@/lib/push');
                      const sub = await ensurePushSubscription();
                      if (sub) toast.success('Push activado en este dispositivo');
                      else toast.error('No se pudo activar (permiso denegado o navegador no soportado)');
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Error al activar push');
                    }
                  }}
                >
                  Activar en este dispositivo
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={async () => {
                    try {
                      const { testPush } = await import('@/lib/push');
                      const r = await testPush();
                      toast.success(r.sent ? `Enviado (${r.sent})` : 'Sin suscripciones en este usuario');
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Error');
                    }
                  }}
                >
                  Enviar prueba
                </button>
              </div>
            </div>
          </div>
        </div>

        {isAdmin && (
          <div className="surface p-5">
            <h2 className="font-display font-semibold">Configurabilidad CRM</h2>
            <p className="text-xs text-muted-foreground mt-1">Glosario, visibilidad, duplicados y motivos de pérdida — sin vertical fijo.</p>

            <div className="mt-4 space-y-4 text-sm">
              <div>
                <h3 className="text-sm font-medium mb-2">Glosario (nombres de entidades)</h3>
                <div className="grid sm:grid-cols-2 gap-2">
                  {GLOSSARY_KEYS.map(([key, label]) => (
                    <label key={key} className="text-xs">
                      {label}
                      <input
                        className="input mt-1"
                        value={glossary[key] || ''}
                        onChange={(e) => setGlossary({ ...glossary, [key]: e.target.value })}
                        placeholder={label}
                      />
                    </label>
                  ))}
                </div>
              </div>

              <div className="border-t border-border pt-3">
                <h3 className="text-sm font-medium mb-2">Visibilidad de leads</h3>
                <label className="text-xs block">
                  Modo global
                  <select
                    className="input mt-1"
                    value={visibility.mode || 'role_default'}
                    onChange={(e) => setVisibility({ ...visibility, mode: e.target.value })}
                  >
                    <option value="role_default">Por rol (admin/supervisor=todos, resto=míos)</option>
                    <option value="all">Todos ven todos</option>
                    <option value="mine">Cada quien solo los suyos</option>
                    <option value="team">Equipo (mismo rol)</option>
                  </select>
                </label>
                <p className="text-[11px] text-muted-foreground mt-1">Puedes afinar por rol abajo (opcional).</p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {['admin', 'supervisor', 'ventas', 'soporte', 'lectura'].map((role) => (
                    <label key={role} className="text-xs">
                      {role}
                      <select
                        className="input mt-1"
                        value={(visibility.by_role || {})[role] || ''}
                        onChange={(e) => setVisibility({
                          ...visibility,
                          by_role: { ...(visibility.by_role || {}), [role]: e.target.value || undefined },
                        })}
                      >
                        <option value="">(usar modo global)</option>
                        <option value="all">all</option>
                        <option value="mine">mine</option>
                        <option value="team">team</option>
                      </select>
                    </label>
                  ))}
                </div>
              </div>

              <div className="border-t border-border pt-3">
                <h3 className="text-sm font-medium mb-2">Duplicados</h3>
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={!!dupRules.by_phone} onChange={(e) => setDupRules({ ...dupRules, by_phone: e.target.checked })} />
                  Detectar por teléfono
                </label>
                <label className="flex items-center gap-2 text-xs mt-1">
                  <input type="checkbox" checked={!!dupRules.by_email} onChange={(e) => setDupRules({ ...dupRules, by_email: e.target.checked })} />
                  Detectar por email
                </label>
                <label className="text-xs block mt-2">
                  Acción al detectar
                  <select className="input mt-1" value={dupRules.action || 'reject'} onChange={(e) => setDupRules({ ...dupRules, action: e.target.value })}>
                    <option value="reject">Rechazar (error 409)</option>
                    <option value="return_existing">Devolver lead existente</option>
                    <option value="allow">Permitir duplicados</option>
                  </select>
                </label>
              </div>

              <div className="border-t border-border pt-3">
                <h3 className="text-sm font-medium mb-2">Motivos de pérdida</h3>
                <label className="flex items-center gap-2 text-xs mb-2">
                  <input type="checkbox" checked={requireLoss} onChange={(e) => setRequireLoss(e.target.checked)} />
                  Exigir motivo al marcar Perdido
                </label>
                <div className="flex flex-wrap gap-1 mb-2">
                  {lossReasons.map((item) => (
                    <span key={item.id} className="chip inline-flex items-center gap-1">
                      {item.label}
                      <button type="button" onClick={() => removeLossReason(item.id)} className="opacity-60 hover:opacity-100">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  {lossReasons.length === 0 && <span className="text-xs text-muted-foreground">Sin motivos aún</span>}
                </div>
                <div className="flex gap-2">
                  <input className="input" value={newLoss} onChange={(e) => setNewLoss(e.target.value)} placeholder="Nuevo motivo" />
                  <button type="button" className="btn-ghost" onClick={addLossReason}><Plus className="h-4 w-4" /></button>
                  <button type="button" className="btn-ghost text-xs" onClick={seedLoss}>Defaults</button>
                </div>
              </div>

              <button type="button" onClick={saveSettings} className="btn-primary"><Save className="h-4 w-4" /> Guardar configurabilidad</button>
            </div>
          </div>
        )}

        <div className="surface p-5">
          <h2 className="font-display font-semibold">WhatsApp</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Elige un modo de conexión por empresa. Los datos de mensajería quedan aislados por tenant.
          </p>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="wa-mode-cloud"
              onClick={() => setMode('cloud_api')}
              className={`rounded-[12px] border p-3 text-left text-sm transition ${waCfg.mode === 'cloud_api' ? 'border-primary bg-primary/10' : 'border-border hover:bg-accent/50'}`}
            >
              <div className="flex items-center gap-2 font-medium"><Cloud className="h-4 w-4" /> API Meta (Cloud)</div>
              <div className="text-xs text-muted-foreground mt-1">Token + Phone Number ID oficiales</div>
            </button>
            <button
              type="button"
              data-testid="wa-mode-qr"
              onClick={() => setMode('qr')}
              className={`rounded-[12px] border p-3 text-left text-sm transition ${waCfg.mode === 'qr' ? 'border-primary bg-primary/10' : 'border-border hover:bg-accent/50'}`}
            >
              <div className="flex items-center gap-2 font-medium"><QrCode className="h-4 w-4" /> Código QR</div>
              <div className="text-xs text-muted-foreground mt-1">WhatsApp Business multi-dispositivo</div>
            </button>
          </div>

          {waCfg.mode === 'cloud_api' && (
            <div className="mt-4 space-y-3 text-sm">
              <p className="text-xs text-muted-foreground">Si no configuras Cloud API, los envíos usarán web.whatsapp.com como fallback.</p>
              <label>Phone Number ID<input value={waCfg.phone_number_id || ''} onChange={(e) => setWaCfg({ ...waCfg, phone_number_id: e.target.value })} className="input mt-1" data-testid="wa-phone-number-id" /></label>
              <label>Access Token<input type="password" value={waCfg.access_token || ''} onChange={(e) => setWaCfg({ ...waCfg, access_token: e.target.value })} className="input mt-1" data-testid="wa-access-token" /></label>
              <label>WABA ID<input value={waCfg.business_account_id || ''} onChange={(e) => setWaCfg({ ...waCfg, business_account_id: e.target.value })} className="input mt-1" /></label>
              <label>Teléfono visible (display)<input value={waCfg.display_phone || ''} onChange={(e) => setWaCfg({ ...waCfg, display_phone: e.target.value })} className="input mt-1" /></label>
              <div className="flex gap-2">
                <button data-testid="wa-cfg-save" onClick={saveWa} className="btn-primary"><Save className="h-4 w-4" /> Guardar Cloud API</button>
                <button onClick={removeWa} className="btn-ghost text-[hsl(var(--danger))]">Remover</button>
              </div>
            </div>
          )}

          {waCfg.mode === 'qr' && (
            <div className="mt-4 space-y-3 text-sm">
              <p className="text-xs text-muted-foreground">
                Abre WhatsApp Business → Dispositivos vinculados → escanea el QR.
                Cada empresa tiene su propia sesión.
              </p>
              <p className="text-xs text-muted-foreground rounded-lg border border-border bg-muted/40 px-3 py-2">
                <strong className="font-medium text-foreground">Historial y contactos:</strong> al vincular, WhatsApp
                envía la agenda del teléfono y chats recientes. Los contactos se convierten en leads (nombre + número);
                los mensajes del historial se adjuntan a leads que ya existan.
              </p>
              <p className="text-xs text-muted-foreground rounded-lg border border-border bg-muted/40 px-3 py-2">
                <strong className="font-medium text-foreground">WhatsApp Web + CRM:</strong> pueden convivir en el mismo PC
                (son dispositivos vinculados distintos). Si ves error 440, el CRM reintenta solo; no hace falta
                cerrar Web salvo que haya dos bridges en el servidor.
              </p>

              {(qrSession?.status === 'connected' || contactSync) && (
                <div className="rounded-[12px] border border-border p-3 space-y-2 bg-card">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <ContactRound className="h-4 w-4 text-emerald-600" />
                    Contactos del teléfono
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Se sincronizan solos al conectar. Usa el botón para reenviar la agenda ya recibida al CRM
                    (WhatsApp no permite redescargar toda la agenda a la carta).
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                    <div className="rounded-lg bg-muted/50 px-2 py-1.5">
                      <div className="text-base font-semibold tabular-nums">{contactSync?.bridge_contacts_cached ?? '—'}</div>
                      <div className="text-[10px] text-muted-foreground">En caché WA</div>
                    </div>
                    <div className="rounded-lg bg-muted/50 px-2 py-1.5">
                      <div className="text-base font-semibold tabular-nums">{contactSync?.leads_from_phonebook ?? 0}</div>
                      <div className="text-[10px] text-muted-foreground">Leads agenda</div>
                    </div>
                    <div className="rounded-lg bg-muted/50 px-2 py-1.5">
                      <div className="text-base font-semibold tabular-nums">{contactSync?.contacts_created ?? 0}</div>
                      <div className="text-[10px] text-muted-foreground">Creados (total)</div>
                    </div>
                    <div className="rounded-lg bg-muted/50 px-2 py-1.5">
                      <div className="text-base font-semibold tabular-nums">{contactSync?.contacts_updated ?? 0}</div>
                      <div className="text-[10px] text-muted-foreground">Actualizados</div>
                    </div>
                  </div>
                  {contactSync?.contacts_sync_at && (
                    <div className="text-[11px] text-muted-foreground">
                      Última sync: {new Date(contactSync.contacts_sync_at).toLocaleString('es-MX')}
                      {contactSync.contacts_sync_source ? ` · ${contactSync.contacts_sync_source}` : ''}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={contactSyncing || qrSession?.status !== 'connected'}
                      onClick={syncPhoneContacts}
                      className="btn-primary h-9 text-xs"
                      title="Reenviar contactos recibidos del teléfono al CRM"
                    >
                      <ContactRound className="h-3.5 w-3.5" />
                      {contactSyncing ? 'Sincronizando…' : 'Sincronizar contactos'}
                    </button>
                    <button type="button" className="btn-ghost h-9 text-xs" onClick={loadContactSync}>
                      <RefreshCw className="h-3.5 w-3.5" /> Actualizar cifras
                    </button>
                    <Link to="/app/leads" className="btn-ghost h-9 text-xs">
                      Ver leads
                    </Link>
                  </div>
                </div>
              )}

              <div className="rounded-[12px] border border-border p-3 flex flex-col sm:flex-row gap-4 items-start">
                <div className="shrink-0 w-[200px] h-[200px] rounded-[12px] bg-white grid place-items-center border border-border overflow-hidden">
                  {qrSession?.qr_data_url ? (
                    <img src={qrSession.qr_data_url} alt="Código QR WhatsApp" className="w-full h-full object-contain" data-testid="wa-qr-image" />
                  ) : (
                    <div className="text-center p-3 text-xs text-muted-foreground">
                      <Smartphone className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      {qrSession?.status === 'connected' ? 'Sesión activa' : 'Sin QR. Pulsa Conectar.'}
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-2 min-w-0">
                  <div>
                    <span className="text-xs text-muted-foreground">Estado</span>
                    <div className={`font-medium ${statusColor[qrSession?.status] || 'text-muted-foreground'}`} data-testid="wa-qr-status">
                      {statusLabel[qrSession?.status] || qrSession?.status || 'Sin iniciar'}
                    </div>
                  </div>
                  {(qrSession?.phone || waCfg.display_phone) && (
                    <div>
                      <span className="text-xs text-muted-foreground">Número vinculado</span>
                      <div className="font-mono text-xs">{qrSession?.phone ? `+${String(qrSession.phone).replace(/^\+/, '')}` : waCfg.display_phone}</div>
                    </div>
                  )}
                  {qrSession?.display_name && (
                    <div>
                      <span className="text-xs text-muted-foreground">Nombre</span>
                      <div>{qrSession.display_name}</div>
                    </div>
                  )}
                  {qrSession?.last_error && (
                    <div className="text-xs text-[hsl(var(--danger))] break-words rounded-lg border border-destructive/30 bg-destructive/5 px-2 py-1.5">
                      {qrSession.last_error}
                    </div>
                  )}
                  {(qrSession?.status === 'qr' || qrSession?.status === 'connecting') && (
                    <div className="text-xs rounded-lg border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-amber-900 dark:text-amber-100">
                      {qrSession?.status === 'qr'
                        ? 'Escanea el código y espera. No pulses de nuevo: al escanear se reinicia la sesión sola.'
                        : 'Vinculando tras el escaneo… no pulses «Nuevo QR» ni recargues hasta ver Conectado.'}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 pt-1">
                    <button
                      data-testid="wa-qr-connect"
                      disabled={qrLoading || qrSession?.status === 'connecting'}
                      onClick={() => connectQr('soft')}
                      className="btn-primary"
                    >
                      <QrCode className="h-4 w-4" />{' '}
                      {qrLoading
                        ? 'Esperando…'
                        : (qrSession?.status === 'connected' ? 'Reconectar' : 'Mostrar / Conectar')}
                    </button>
                    <button
                      data-testid="wa-qr-refresh"
                      disabled={qrLoading || qrSession?.status === 'connecting' || qrSession?.status === 'connected'}
                      onClick={() => connectQr('new_qr')}
                      className="btn-ghost"
                      title={qrSession?.status === 'connected' ? 'Desvincula primero' : 'Generar código limpio'}
                    >
                      <RefreshCw className={`h-4 w-4 ${qrLoading ? 'animate-spin' : ''}`} /> Nuevo QR
                    </button>
                    <button data-testid="wa-qr-status-refresh" disabled={qrLoading} onClick={loadQrStatus} className="btn-ghost">
                      Actualizar estado
                    </button>
                    {(qrSession?.status === 'connected' || qrSession?.status === 'connecting' || qrSession?.status === 'qr') && (
                      <button data-testid="wa-qr-disconnect" disabled={qrLoading} onClick={disconnectQr} className="btn-ghost text-[hsl(var(--danger))]">
                        <Unplug className="h-4 w-4" /> Desvincular
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <button data-testid="wa-cfg-save" onClick={saveWa} className="btn-ghost"><Save className="h-4 w-4" /> Guardar modo QR</button>
                <button onClick={removeWa} className="btn-ghost text-[hsl(var(--danger))]">Remover config</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
